import { templateStatus } from '@/theme';
import type {
  CategoriaMetaPlantilla,
  CategoriaPlantilla,
  EventoPlantilla,
  EstadoPlantilla,
  TipoEncabezadoPlantilla,
} from '@/types/domain';
import type {
  CampoTemplate,
  EventoCatalogo,
  PlantillaParametro,
  SeedTemplatesResult,
  SendTemplateResult,
  SyncTemplatesResult,
  TemplateCatalog,
  WhatsappTemplate,
} from '@/types/templates';

/**
 * Formato de las plantillas para la pantalla.
 *
 * Igual que en Campañas y Órdenes, lo que se pinta no se parece a lo que devuelve la API: los estados
 * de Meta se traducen (ver `templateStatus` en `@/theme`), el mapeo `{{n}}` → campo se lee como una
 * frase, y los resúmenes de cabecera se arman acá para poder probarlos sin navegador
 * (`smoke-templates.tsx`).
 *
 * Dos decisiones del módulo:
 *  - el «enviable» es del backend (`estado === 'APROBADA' && activo`) y solo se explica, nunca se
 *    recalcula: si el negocio apaga la plantilla, deja de usarse pero sigue aprobada en Meta;
 *  - las fechas se formatean con `@/lib/format` y acá solo se les pone la frase («Sincronizada el …»).
 */

const LOCALE = 'es-CO';

/** Etiqueta en español de cada evento del sistema (el catálogo trae la descripción, no el nombre). */
export const EVENTO_LABELS: Record<EventoPlantilla, string> = {
  CONFIRMACION_PEDIDO: 'Confirmación del pedido',
  PEDIDO_ENVIADO: 'Pedido enviado (con la guía)',
  RECORDATORIO_PAGO: 'Recordatorio de pago',
  CARRITO_ABANDONADO: 'Carrito abandonado',
  ACTUALIZACION_ESTADO: 'Actualización del estado del pedido',
  NUEVA_VENTA_ADMIN: 'Nueva venta (aviso al admin)',
  CANCELACION_ADMIN: 'Cancelación (aviso al admin)',
  ATENCION_HUMANA_ADMIN: 'Atención humana pedida',
  JSON_INVALIDO_ADMIN: 'JSON de pedido inválido',
};

/** Orden de los eventos en los `<select>` y en la tabla (los del cliente primero, como el backend). */
export const EVENTO_ORDER: EventoPlantilla[] = [
  'CONFIRMACION_PEDIDO',
  'PEDIDO_ENVIADO',
  'RECORDATORIO_PAGO',
  'CARRITO_ABANDONADO',
  'ACTUALIZACION_ESTADO',
  'NUEVA_VENTA_ADMIN',
  'CANCELACION_ADMIN',
  'ATENCION_HUMANA_ADMIN',
  'JSON_INVALIDO_ADMIN',
];

/** «Al cliente» / «Al administrador». */
export const CATEGORIA_LABELS: Record<CategoriaPlantilla, string> = {
  CLIENTE: 'Al cliente',
  ADMIN: 'Al administrador',
};

/** Categoría con la que Meta revisa la plantilla (afecta el costo y las reglas de la plantilla). */
export const CATEGORIA_META_LABELS: Record<CategoriaMetaPlantilla, string> = {
  UTILITY: 'Utility (utilidad)',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Authentication (autenticación)',
};

/** Tipo de encabezado. */
export const ENCABEZADO_LABELS: Record<TipoEncabezadoPlantilla, string> = {
  NINGUNO: 'Sin encabezado',
  TEXTO: 'Texto',
  IMAGEN: 'Imagen',
};

/** Todas las categorías de Meta, para el `<select>` del formulario. */
export const CATEGORIA_META_OPTIONS: CategoriaMetaPlantilla[] = [
  'UTILITY',
  'MARKETING',
  'AUTHENTICATION',
];

/** Todas las categorías locales, para el `<select>` del formulario. */
export const CATEGORIA_OPTIONS: CategoriaPlantilla[] = ['CLIENTE', 'ADMIN'];

/** Todos los tipos de encabezado, para el `<select>` del formulario. */
export const ENCABEZADO_OPTIONS: TipoEncabezadoPlantilla[] = ['NINGUNO', 'TEXTO', 'IMAGEN'];

/** Nombre del evento tal como se pinta en la pantalla. */
export function eventoLabel(evento: EventoPlantilla): string {
  return EVENTO_LABELS[evento] ?? evento;
}

/** Estado de Meta con su etiqueta y su tono (`@/theme/status.ts`). */
export function templateStatusMeta(estado: EstadoPlantilla) {
  return templateStatus[estado];
}

/**
 * Posiciones `{{n}}` usadas en el cuerpo, únicas y ordenadas.
 *
 * Espejo exacto de `variablesDelCuerpo` del backend: el formulario necesita saber qué mapear ANTES de
 * mandar nada (y el backend valida lo mismo, así que un error acá no se puede colar).
 */
export function variablesDelCuerpo(cuerpo: string): number[] {
  const encontradas = new Set<number>();

  for (const match of cuerpo.matchAll(/\{\{(\d+)\}\}/g)) {
    const posicion = Number(match[1]);
    if (Number.isInteger(posicion) && posicion > 0) encontradas.add(posicion);
  }

  return [...encontradas].sort((a, b) => a - b);
}

/** Mapeo ordenado por posición (el backend también lo normaliza así). */
export function sortParametros(parametros: PlantillaParametro[]): PlantillaParametro[] {
  return [...parametros].sort((a, b) => a.posicion - b.posicion);
}

/** El campo mapeado a una posición, si hay. */
export function campoDePosicion(
  parametros: PlantillaParametro[],
  posicion: number,
): PlantillaParametro | undefined {
  return parametros.find((parametro) => parametro.posicion === posicion);
}

/** Etiqueta del campo (`Nombre del cliente`) o la clave cruda si el catálogo no lo conoce. */
export function campoEtiqueta(campos: CampoTemplate[], clave: string | undefined): string {
  if (!clave) return 'sin mapear';
  return campos.find((campo) => campo.clave === clave)?.etiqueta ?? clave;
}

/** «{{1}} → Nombre del cliente · {{2}} → Total del pedido» (el mapeo de la tabla y del detalle). */
export function describeParametros(
  parametros: PlantillaParametro[],
  campos: CampoTemplate[],
): string {
  const ordenados = sortParametros(parametros);
  if (ordenados.length === 0) return 'Sin variables: el texto va tal cual';

  return ordenados
    .map((parametro) => `{{${parametro.posicion}}} → ${campoEtiqueta(campos, parametro.campo)}`)
    .join(' · ');
}

/** «Sin encabezado» / «Encabezado de texto: “Tu pedido va en camino”» / «Encabezado de imagen». */
export function describeEncabezado(plantilla: {
  encabezadoTipo: TipoEncabezadoPlantilla;
  encabezadoTexto: string | null;
}): string {
  if (plantilla.encabezadoTipo === 'TEXTO') {
    return `Encabezado de texto: «${plantilla.encabezadoTexto ?? ''}»`;
  }
  if (plantilla.encabezadoTipo === 'IMAGEN') {
    return 'Encabezado de imagen (la URL viaja al enviar)';
  }
  return 'Sin encabezado';
}

/** «pedido_confirmado · es»: lo que identifica la plantilla en el WABA. */
export function describeIdentidad(
  plantilla: Pick<WhatsappTemplate, 'nombreMeta' | 'idioma'>,
): string {
  return `${plantilla.nombreMeta} · ${plantilla.idioma}`;
}

/** «Sincronizada el 16 sept 2026, 14:05» / «Todavía no se sincronizó con Meta» (fecha ya formateada). */
export function describeUltimaSync(formatted: string): string {
  return formatted === '—' ? 'Todavía no se sincronizó con Meta' : `Sincronizada el ${formatted}`;
}

/** «12» / «1.250,5» (es-CO). */
export function formatNumber(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 2 });
}

/** «3 plantillas» / «1 plantilla» (plural explícito: en es no se pluraliza solo). */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/**
 * Resumen del encabezado: cuántas plantillas hay, cuántas se pueden usar hoy y en qué estado están.
 *
 * `enviable` lo calcula el backend por fila (`APROBADA` + `activo`), así que el conteo no reinterpreta
 * el estado: una APROBADA que el negocio apagó no entra.
 */
export function summarizeTemplates(templates: WhatsappTemplate[]): string {
  if (templates.length === 0) return 'Todavía no hay plantillas registradas';

  const enviables = templates.filter((plantilla) => plantilla.enviable).length;
  const borradores = templates.filter((plantilla) => plantilla.estado === 'BORRADOR').length;
  const enRevision = templates.filter((plantilla) => plantilla.estado === 'PENDIENTE').length;

  const partes = [
    countLabel(templates.length, 'plantilla registrada', 'plantillas registradas'),
    `${formatNumber(enviables)} enviable${enviables === 1 ? '' : 's'} hoy`,
  ];

  if (borradores > 0) partes.push(countLabel(borradores, 'borrador', 'borradores'));
  if (enRevision > 0) partes.push(countLabel(enRevision, 'en revisión de Meta', 'en revisión de Meta'));

  return partes.join(' · ');
}

/** «9 eventos · 4 ya tienen plantilla»: el estado del onboarding en el catálogo. */
export function summarizeCatalog(catalogo: TemplateCatalog): string {
  const conPlantilla = catalogo.eventos.filter((evento) => evento.plantilla !== null).length;

  const partes = [
    countLabel(catalogo.eventos.length, 'evento del sistema', 'eventos del sistema'),
    conPlantilla === 0
      ? 'ninguno tiene plantilla'
      : `${formatNumber(conPlantilla)} ya ${conPlantilla === 1 ? 'tiene' : 'tienen'} plantilla`,
  ];

  return partes.join(' · ');
}

/** ¿El negocio ya tiene su plantilla para este evento? (lo dice el catálogo). */
export function tienePlantilla(evento: EventoCatalogo): boolean {
  return evento.plantilla !== null;
}

/**
 * Lo que se cuenta cuando termina «Sembrar recomendadas»: cuántos borradores se crearon, qué eventos
 * ya tenían plantilla y qué falló al mandarlos a Meta (el borrador queda creado igual).
 */
export function summarizeSeed(result: SeedTemplatesResult): string {
  const partes: string[] = [];

  partes.push(
    result.creadas === 0
      ? 'No había borradores que crear'
      : `Se ${result.creadas === 1 ? 'creó' : 'crearon'} ${countLabel(
          result.creadas,
          'borrador',
          'borradores',
        )} con los textos del catálogo`,
  );

  if (result.existentes.length > 0) {
    partes.push(
      countLabel(result.existentes.length, 'evento ya tenía plantilla', 'eventos ya tenían plantilla'),
    );
  }

  if (result.errores.length > 0) {
    partes.push(
      countLabel(result.errores.length, 'falló al enviarse a Meta', 'fallaron al enviarse a Meta'),
    );
  }

  return `${partes.join(', ')}.`;
}

/** Lo que se cuenta cuando termina «Sincronizar con Meta». */
export function summarizeSync(result: SyncTemplatesResult): string {
  const partes = [
    countLabel(result.revisadas, 'plantilla revisada', 'plantillas revisadas'),
    `${formatNumber(result.actualizadas)} ${
      result.actualizadas === 1 ? 'cambió de estado' : 'cambiaron de estado'
    }`,
  ];

  if (result.noEncontradas.length > 0) {
    partes.push(
      `Meta no tiene ${countLabel(
        result.noEncontradas.length,
        'de estas plantillas locales',
        'de estas plantillas locales',
      )}`,
    );
  }

  return `${partes.join(' · ')}.`;
}

/** Lo que se cuenta después de una prueba enviada: a quién fue y con qué id salió. */
export function summarizeSend(result: SendTemplateResult, to: string): string {
  const destino = to.trim();

  if (!result.enviado) {
    return `Meta no aceptó el envío hacia ${destino}${result.motivo ? ` (motivo: ${result.motivo})` : ''}.`;
  }

  const partes = [`La plantilla salió hacia ${destino}`];
  if (result.fueraDeVentana) partes.push('Meta avisó que la ventana de 24 h estaba cerrada');

  return `${partes.join(' · ')}.`;
}

/** Todo lo que se puede buscar en una plantilla (nombre, idioma, evento, cuerpo y mapeo). */
export function templateSearchText(plantilla: WhatsappTemplate): string {
  return [
    plantilla.nombreMeta,
    plantilla.idioma,
    plantilla.evento,
    eventoLabel(plantilla.evento),
    plantilla.cuerpo,
    plantilla.pie ?? '',
    plantilla.parametros.map((parametro) => parametro.campo).join(' '),
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Buscador de la tabla sobre las plantillas YA cargadas.
 *
 * El reparto con el servidor es deliberado (igual que en Campañas): `evento`, `categoria` y `estado`
 * viajan al backend (son query params reales) mientras la búsqueda se resuelve acá. No se manda al
 * servidor porque el endpoint NO pagina —devuelve el array entero— y escribir letra a letra no debe
 * disparar una petición por tecla.
 */
export function filterTemplates(
  templates: WhatsappTemplate[],
  search: string | undefined,
): WhatsappTemplate[] {
  const needle = search?.trim().toLowerCase() ?? '';
  if (needle.length === 0) return templates;

  return templates.filter((plantilla) => templateSearchText(plantilla).includes(needle));
}



